/**
 * Consulta manual: `POST /createmanualinquiry` + `PATCH /assigninquiryrecord`
 * (APIBOT M1 `4bb45b6` + M2/M3 `50cd4ef`, DEV).
 * Contrato: argenlotapi/docs/frontend/FRONTEND_LEADS.md § Consulta manual.
 * Map: docs/maps/leads.md § Consulta manual.
 *
 * El dueño o el editor del espacio (`owner_user_id` en el body, como
 * `createlead`) carga una consulta que le llegó por otro canal, para un lead
 * existente (`lead_id`) o uno nuevo (`lead`). No manda mail; nace vista.
 * M2: 0 a 20 registros (`records: [{ item_kind, id }]`): una consulta + un
 * interés por registro, en una transacción. Sin registro = consulta abierta
 * (se asigna después con `assignInquiryRecord`).
 */
import { patch, post } from './httpClient';
import { spaceOwnerQueryParams } from './spaceMembersService';
import {
  LEADS_NETWORK_MESSAGE,
  RECORD_ITEM_KINDS,
  getLeadErrorMessage,
  readApiErrorCode,
  readLeadDuplicateId,
} from './leadsService';
import { emitCommercialChanged } from '../Leads/commercialSummary';

/** Canales que acepta la API (`share_form` es solo del form del share). */
export const MANUAL_INQUIRY_CHANNELS = ['phone', 'whatsapp', 'in_person', 'social', 'portal', 'other'];
export const MANUAL_INQUIRY_MESSAGE_MAX = 2000;
/** M2: 0 a 20 registros por carga. */
export const MANUAL_INQUIRY_MAX_RECORDS = 20;

export const MANUAL_INQUIRY_NOT_ENABLED_MESSAGE = 'Todavía no se pueden cargar consultas manuales. Probá más tarde.';
export const MANUAL_INQUIRY_RATE_LIMIT_MESSAGE = 'Llegaste al tope de 200 consultas manuales por día. Probá mañana.';
export const MANUAL_INQUIRY_OWNER_EMAIL_MESSAGE = 'Cargá el email del cliente: no puede ser el tuyo ni el del dueño del espacio.';
export const MANUAL_INQUIRY_RECORD_INVALID_MESSAGE = 'Ese registro ya no está en este espacio. Elegí otro.';
export const MANUAL_INQUIRY_LEAD_NOT_FOUND_MESSAGE = 'Ese lead ya no existe. Elegí otro o cargá uno nuevo.';
export const MANUAL_INQUIRY_FORBIDDEN_MESSAGE = 'No tenés permiso para cargar consultas en este espacio.';
export const MANUAL_INQUIRY_DUPLICATE_MESSAGE = 'Ya existe un lead con ese email o teléfono.';
export const MANUAL_INQUIRY_TOO_MANY_RECORDS_MESSAGE = 'Elegí hasta 20 registros por consulta.';
export const ASSIGN_RECORD_NOT_FOUND_MESSAGE = 'Esta consulta ya no existe. Actualizamos el lead.';
export const ASSIGN_RECORD_NOT_MANUAL_MESSAGE = 'Solo se puede asignar registro a una consulta cargada a mano.';
export const ASSIGN_RECORD_ALREADY_SET_MESSAGE = 'Esta consulta ya tiene un registro. Actualizamos el lead.';

const NETWORK_ERROR = Object.freeze({ _httpOk: false, _httpStatus: null, error: LEADS_NETWORK_MESSAGE });

// 503 MANUAL_INQUIRY_NOT_ENABLED → se oculta «Nueva consulta» el resto de la sesión.
let manualInquiryDisabled = false;

/** ¿La API dijo que la carga manual no está habilitada (503) en esta sesión? */
export function isManualInquiryDisabled() {
  return manualInquiryDisabled;
}

/** Solo tests: vuelve a mostrar el botón. */
export function resetManualInquiryAvailability() {
  manualInquiryDisabled = false;
}

function text(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function missing(message, field) {
  return { _httpOk: false, _httpStatus: 400, code: 'VALIDATION_ERROR', field, error: message, _local: true };
}

/**
 * `occurred_at` ISO con offset, o '' si no hay. Acepta Date, ISO o el valor
 * de un `<input type="datetime-local">` (`YYYY-MM-DDTHH:mm`, hora local).
 *
 * @param {Date|string|null|undefined} value
 * @returns {string}
 */
export function toManualInquiryOccurredAt(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : '';
}

/**
 * POST /createmanualinquiry.
 *
 * @param {{ leadId?: string, lead?: { name: string, email?: string, phone?: string },
 *   channel?: string, message: string, occurredAt?: Date|string|null,
 *   records?: { itemKind: string, id: string }[] }} body
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 * @returns {Promise<object>} Envelope de httpClient.
 */
export async function createManualInquiry(body = {}, { ownerUserId, signal } = {}) {
  const payload = {};
  const leadId = text(body.leadId);
  if (leadId) {
    payload.lead_id = leadId;
  } else {
    const lead = {};
    ['name', 'email', 'phone'].forEach((key) => {
      const value = text(body.lead?.[key]);
      if (value) lead[key] = value;
    });
    if (!lead.name) return missing('Cargá el nombre.', 'name');
    if (!lead.email && !lead.phone) return missing('Cargá al menos un teléfono o un email.', 'contact');
    payload.lead = lead;
  }
  const message = text(body.message);
  if (!message) return missing('Escribí qué consultó.', 'message');
  payload.message = message.slice(0, MANUAL_INQUIRY_MESSAGE_MAX);
  const channel = text(body.channel);
  if (channel && MANUAL_INQUIRY_CHANNELS.includes(channel)) payload.channel = channel;
  const occurredAt = toManualInquiryOccurredAt(body.occurredAt);
  if (occurredAt) payload.occurred_at = occurredAt;
  payload.records = buildManualInquiryRecords(body.records);
  if (payload.records.length > MANUAL_INQUIRY_MAX_RECORDS) {
    return missing(MANUAL_INQUIRY_TOO_MANY_RECORDS_MESSAGE, 'records');
  }

  let res;
  try {
    res = await post('/createmanualinquiry', {
      body: { ...payload, ...spaceOwnerQueryParams(ownerUserId) },
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return { ...NETWORK_ERROR };
  }
  if (res?._httpStatus === 503 && readApiErrorCode(res) === 'MANUAL_INQUIRY_NOT_ENABLED') {
    manualInquiryDisabled = true;
  }
  // Con registro cambia el resumen comercial (interés nuevo / reactivado, `inquiries_count`).
  if (res?._httpOk && payload.records.length) {
    emitCommercialChanged({ reason: 'manual_inquiry', leadId: res?.lead_id || leadId || null });
  }
  return res;
}

/**
 * `records` del body: `{ itemKind, id }` válidos → `{ item_kind, id }`, sin
 * repetidos (la API también los ignora y los cuenta en `duplicates_ignored`).
 *
 * @param {{ itemKind: string, id: string }[]|null|undefined} records
 * @returns {{ item_kind: string, id: string }[]}
 */
export function buildManualInquiryRecords(records) {
  const seen = new Set();
  const out = [];
  (Array.isArray(records) ? records : []).forEach((rec) => {
    const itemKind = text(rec?.itemKind);
    const id = text(rec?.id);
    const key = `${itemKind}:${id}`;
    if (!RECORD_ITEM_KINDS.includes(itemKind) || !id || seen.has(key)) return;
    seen.add(key);
    out.push({ item_kind: itemKind, id });
  });
  return out;
}

/**
 * PATCH /assigninquiryrecord: registro para una consulta manual abierta.
 *
 * @param {{ inquiryId: string, record: { itemKind: string, id: string } }} body
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 * @returns {Promise<object>} Envelope; 200 `{ data, lead_id, interest }`.
 */
export async function assignInquiryRecord(body = {}, { ownerUserId, signal } = {}) {
  const inquiryId = text(body.inquiryId);
  if (!inquiryId) return missing('Falta la consulta.', 'inquiry_id');
  const [record] = buildManualInquiryRecords([body.record]);
  if (!record) return missing('Elegí un registro.', 'record');
  let res;
  try {
    res = await patch('/assigninquiryrecord', {
      body: { inquiry_id: inquiryId, record, ...spaceOwnerQueryParams(ownerUserId) },
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return { ...NETWORK_ERROR };
  }
  if (res?._httpOk) emitCommercialChanged({ reason: 'inquiry_record_assigned', leadId: res?.lead_id || null });
  return res;
}

/**
 * Error de /assigninquiryrecord en castellano. `refresh`: la consulta cambió
 * (borrada / ya asignada): conviene recargar el lead.
 *
 * @param {object} res
 * @returns {{ message: string, refresh: boolean }}
 */
export function describeAssignRecordError(res) {
  const code = readApiErrorCode(res);
  const status = res?._httpStatus;
  if (code === 'INQUIRY_NOT_FOUND' || status === 404) return { message: ASSIGN_RECORD_NOT_FOUND_MESSAGE, refresh: true };
  if (code === 'INQUIRY_RECORD_ALREADY_SET') return { message: ASSIGN_RECORD_ALREADY_SET_MESSAGE, refresh: true };
  if (code === 'INQUIRY_NOT_MANUAL') return { message: ASSIGN_RECORD_NOT_MANUAL_MESSAGE, refresh: true };
  if (code === 'RECORD_REF_INVALID') return { message: MANUAL_INQUIRY_RECORD_INVALID_MESSAGE, refresh: false };
  if (code === 'SPACE_FORBIDDEN' || status === 403) return { message: MANUAL_INQUIRY_FORBIDDEN_MESSAGE, refresh: false };
  if (status == null) return { message: res?.error || LEADS_NETWORK_MESSAGE, refresh: false };
  return { message: text(res?.error) || 'No se pudo asignar el registro.', refresh: false };
}

/**
 * @param {object} res Envelope 201 de /createmanualinquiry.
 * `data` / `interest` = la primera consulta / interés (compat M1); con
 * varios registros, `inquiries` / `interests` (en el orden de `records`).
 *
 * @returns {{ inquiry: object|null, inquiries: object[], leadId: string, leadCreated: boolean,
 *   unarchived: boolean, interest: object|null, interests: object[], duplicatesIgnored: number }}
 */
export function readManualInquiryResult(res) {
  const inquiry = res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : null;
  const readInterest = (raw) => (raw && typeof raw === 'object' && raw.id
    ? { id: String(raw.id), created: raw.created === true, reactivated: raw.reactivated === true }
    : null);
  const interests = (Array.isArray(res?.interests) ? res.interests : []).map(readInterest).filter(Boolean);
  const interest = readInterest(res?.interest) || interests[0] || null;
  const inquiries = Array.isArray(res?.inquiries) && res.inquiries.length
    ? res.inquiries.filter((row) => row && typeof row === 'object')
    : (inquiry ? [inquiry] : []);
  const dup = Number(res?.duplicates_ignored);
  return {
    inquiry: inquiry || inquiries[0] || null,
    inquiries,
    leadId: text(res?.lead_id) || text(inquiry?.lead_id),
    leadCreated: res?.lead_created === true,
    unarchived: res?.unarchived === true,
    interest,
    interests: interests.length ? interests : (interest ? [interest] : []),
    duplicatesIgnored: Number.isFinite(dup) && dup > 0 ? Math.trunc(dup) : 0,
  };
}

// `field` de la API → campo del formulario.
const FIELD_MAP = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  contact: 'contact',
  message: 'message',
  channel: 'channel',
  occurred_at: 'occurredAt',
  records: 'record',
  record: 'record',
  item_kind: 'record',
  id: 'record',
  lead_id: 'lead',
  lead: 'lead',
};

/**
 * Error de /createmanualinquiry para la UI: texto en castellano + campo a
 * marcar (`name|email|phone|contact|message|channel|occurredAt|record|lead`)
 * o '' (alerta general). 409 → `duplicateId` para «Usar el lead existente».
 *
 * @param {object} res Envelope de error.
 * `RECORD_REF_INVALID` trae `index` (posición en `records`) → `recordIndex`.
 *
 * @returns {{ message: string, field: string, duplicateId: string, disabled: boolean,
 *   recordIndex: number|null }}
 */
export function describeManualInquiryError(res) {
  const status = res?._httpStatus;
  const code = readApiErrorCode(res);
  const apiText = text(res?.error);
  const indexRaw = Number(res?.index);
  const recordIndex = res?.index !== undefined && res?.index !== null && Number.isInteger(indexRaw) && indexRaw >= 0
    ? indexRaw : null;
  const out = (message, field = '', extra = {}) => ({
    message, field, duplicateId: '', disabled: false, recordIndex: field === 'record' ? recordIndex : null, ...extra,
  });

  const duplicateId = readLeadDuplicateId(res);
  if (duplicateId) return out(MANUAL_INQUIRY_DUPLICATE_MESSAGE, '', { duplicateId });
  if (code === 'INQUIRY_OWNER_EMAIL') return out(MANUAL_INQUIRY_OWNER_EMAIL_MESSAGE, 'email');
  if (code === 'RECORD_REF_INVALID') {
    return out(recordIndex !== null
      ? 'Ese registro ya no está en este espacio. Sacalo y probá de nuevo; no se guardó nada.'
      : MANUAL_INQUIRY_RECORD_INVALID_MESSAGE, 'record');
  }
  if (code === 'LEAD_NOT_FOUND' || (status === 404 && !code)) return out(MANUAL_INQUIRY_LEAD_NOT_FOUND_MESSAGE, 'lead');
  if (code === 'SPACE_FORBIDDEN' || status === 403) return out(MANUAL_INQUIRY_FORBIDDEN_MESSAGE);
  if (code === 'MANUAL_INQUIRY_RATE_LIMIT' || status === 429) return out(MANUAL_INQUIRY_RATE_LIMIT_MESSAGE);
  if (code === 'MANUAL_INQUIRY_NOT_ENABLED' || status === 503) {
    return out(MANUAL_INQUIRY_NOT_ENABLED_MESSAGE, '', { disabled: true });
  }
  if (status === 400 && (code === 'VALIDATION_ERROR' || res?.field)) {
    const field = FIELD_MAP[String(res?.field || '').trim()] || '';
    if (field === 'occurredAt' && /futur/i.test(apiText)) return out('La fecha no puede ser futura.', field);
    if (field === 'record' && res?.max !== undefined) return out(MANUAL_INQUIRY_TOO_MANY_RECORDS_MESSAGE, field);
    return out(apiText || 'Revisá los datos de la consulta.', field);
  }
  if (status == null) return out(res?.error || LEADS_NETWORK_MESSAGE);
  return out(getLeadErrorMessage(res, 'No se pudo cargar la consulta.'));
}
